import React, { useMemo, useState } from "react";
import { ArrowUpRight, ChevronDown, Loader2, Star } from "lucide-react";
import { STATUS_META } from "../mock";
import { getPriceComparison, getSortPrice } from "../data/registrarPrices";
import { formatMoney } from "../data/currency";

const AVAILABLE_STATUSES = ["available", "premium", "marketed"];

function DomainLabel({ result }) {
  return (
    <div className="min-w-0 flex-1">
      <span className="font-mono text-lg tracking-tight sm:text-xl">
        <span className="text-zinc-500">{result.name}</span>
        <span className="font-semibold text-white">.{result.tld}</span>
      </span>
      {result.isHack && (
        <span className="ml-3 align-middle text-[11px] uppercase tracking-wide text-zinc-600">
          domain hack
        </span>
      )}
      {result.live && (
        <span className="ml-3 inline-flex items-center gap-1 align-middle text-[10px] font-medium uppercase tracking-wide text-emerald-400/80">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          live
        </span>
      )}
    </div>
  );
}

function PriceComparison({ domain, tld, currency }) {
  const rows = getPriceComparison(tld, domain);
  return (
    <div>
      <p className="mb-3 text-xs uppercase tracking-widest text-zinc-600">
        Compare registrars
      </p>
      <div className="overflow-hidden rounded-lg border border-white/10">
        <div className="hidden grid-cols-[1.5fr_1fr_1fr_auto] gap-3 border-b border-white/5 bg-white/[0.02] px-4 py-2 text-[11px] uppercase tracking-wide text-zinc-600 sm:grid">
          <span>Registrar</span>
          <span>First year</span>
          <span>Renews</span>
          <span className="w-16 text-right">Buy</span>
        </div>

        {rows.map((r, i) => (
          <div
            key={r.key}
            className={`flex flex-col gap-3 px-4 py-3 sm:grid sm:grid-cols-[1.5fr_1fr_1fr_auto] sm:items-center ${
              i ? "border-t border-white/5" : ""
            } ${r.bestValue ? "bg-emerald-500/[0.06]" : ""}`}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium text-zinc-100">{r.name}</span>
              {r.bestValue && (
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-400 ring-1 ring-emerald-500/30">
                  Best value
                </span>
              )}
            </div>

            <div className="text-sm">
              <span className="text-zinc-500 sm:hidden">First year: </span>
              <span className="font-semibold text-white">{formatMoney(r.first, currency)}</span>
              <span className="text-zinc-600">/yr</span>
            </div>

            <div className="text-sm">
              <span className="text-zinc-500 sm:hidden">Renews: </span>
              <span className="text-zinc-300">{formatMoney(r.renew, currency)}/yr</span>
            </div>

            <a
              href={r.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`buy-${r.key}-btn`}
              className="rounded-md bg-emerald-500 px-4 py-1.5 text-center text-xs font-semibold text-black transition-colors hover:bg-emerald-400 sm:w-16 sm:justify-self-end"
            >
              Buy
            </a>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-zinc-600">
        Indicative prices in {currency}; the registrar confirms the final price at
        checkout. “Best value” = lowest ongoing renewal rate.
      </p>
    </div>
  );
}

function ResultDetails({ result, meta, showRegistrars, currency }) {
  return (
    <div className="bg-white/[0.02] px-5 pb-5 pt-1 sm:px-12">
      <p className="mb-4 text-sm text-zinc-500">{meta.desc}</p>

      {showRegistrars ? (
        <PriceComparison domain={result.domain} tld={result.tld} currency={currency} />
      ) : (
        <a
          href={`https://${result.domain}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-zinc-400 transition-colors hover:text-white"
        >
          Visit {result.domain} <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

function ResultRow({ result, index, expanded, onToggle, currency, isFavourite, onToggleFavourite }) {
  const meta = STATUS_META[result.status];
  const showRegistrars = result.status !== "taken" && result.status !== "tld";

  return (
    <div
      className="result-row border-b border-white/5"
      style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
    >
      <div className="group flex w-full items-center gap-4 px-4 py-4 transition-colors hover:bg-white/[0.03] sm:px-5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: meta.color, boxShadow: `0 0 10px ${meta.color}55` }}
        />
        <button
          onClick={() => onToggle(result.domain)}
          className="flex min-w-0 flex-1 items-center gap-4 text-left"
          data-testid={`result-toggle-${result.domain}`}
        >
          <DomainLabel result={result} />
          <span className="shrink-0 text-sm font-medium" style={{ color: meta.color }}>
            {meta.label}
          </span>
        </button>
        <button
          onClick={() => onToggleFavourite(result.domain)}
          data-testid={`favourite-${result.domain}`}
          aria-label={isFavourite ? "Remove from saved" : "Save domain"}
          className={`shrink-0 transition-colors ${
            isFavourite ? "text-amber-400" : "text-zinc-600 hover:text-amber-400"
          }`}
        >
          <Star className="h-4 w-4" fill={isFavourite ? "currentColor" : "none"} />
        </button>
        <button onClick={() => onToggle(result.domain)} className="shrink-0" aria-label="Expand">
          <ChevronDown
            className={`h-4 w-4 text-zinc-600 transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {expanded && (
        <ResultDetails
          result={result}
          meta={meta}
          showRegistrars={showRegistrars}
          currency={currency}
        />
      )}
    </div>
  );
}

export default function SearchResults({
  results,
  loading,
  query,
  currency,
  sortBy,
  availableOnly,
  favourites,
  onToggleFavourite,
}) {
  const [expanded, setExpanded] = useState(null);

  const toggle = (domain) => setExpanded((cur) => (cur === domain ? null : domain));

  const displayed = useMemo(() => {
    let list = results;
    if (availableOnly) {
      list = list.filter((r) => AVAILABLE_STATUSES.includes(r.status));
    }
    if (sortBy === "price") {
      list = [...list].sort((a, b) => getSortPrice(a.tld) - getSortPrice(b.tld));
    } else if (sortBy === "az") {
      list = [...list].sort((a, b) => a.domain.localeCompare(b.domain));
    }
    return list;
  }, [results, availableOnly, sortBy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Checking availability…</span>
      </div>
    );
  }

  if (query && displayed.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-zinc-500" data-testid="no-results">
        {availableOnly
          ? "No available domains for this search. Turn off “Available only” to see all."
          : "No results. Try a different search."}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0e0e10]" data-testid="search-results">
      {displayed.map((r, i) => (
        <ResultRow
          key={r.domain}
          result={r}
          index={i}
          expanded={expanded === r.domain}
          onToggle={toggle}
          currency={currency}
          isFavourite={favourites.includes(r.domain)}
          onToggleFavourite={onToggleFavourite}
        />
      ))}
    </div>
  );
}
